<script lang="ts">
  import { Form, Validate } from "$shared/lib/validation_next";
  import { push } from "svelte-spa-router";
  import api, { fromApi } from "$parkito-api";
  import {
    DateModel,
    IntervalModel,
    MyPagesSpotModel,
    SpotClassModel,
  } from "$models";
  import {
    BodyText,
    Button,
    CardSection,
    Hor,
    Label,
    ModalDialog,
    Pill,
    Spacer,
    Toast,
    ValInputField,
    Ver,
  } from "$components";

  type Props =
    & ({
      spotClass: SpotClassModel;
      spotGroup?: MyPagesSpotModel;
    })
    & (
      | {
        /// If you want checkout button then
        /// siteId and queueId must be provided
        siteId?: undefined;
        queueId?: undefined;
        startDate?: undefined;
      }
      | {
        siteId: string;
        queueId: string;
        startDate: DateModel;
      }
    );

  const {
    spotClass,
    siteId,
    queueId,
    startDate,
    spotGroup,
  }: Props = $props();

  const availabilityType: "error" | "warning" | "success" =
    spotClass.availability === "full"
      ? "error"
      : spotClass.availability === "almostFull"
      ? "warning"
      : "success";
  const availabilityText: string = spotClass.availability === "full"
    ? "Fullt"
    : spotClass.availability === "almostFull"
    ? "Nästan fullt"
    : "God tillgänglighet";

  const buttonText = spotClass.productKind === "Permit"
    ? "Köp direkt"
    : "Anmäl intresse";
  const buttonType = spotClass.productKind === "Permit"
    ? "secondary"
    : "primary";
  const canClickButton = spotClass.productKind === "Reserved" ||
    spotClass.availability !== "full";

  const agreementPeriod = spotGroup?.agreementPeriod ??
    new IntervalModel({ days: 0, micros: 0, months: 1 });

  const requiresAccessCode = !!spotGroup?.accessCodeRef;

  let showAccessCodeDialog = $state(false);
  const toggleAccessCodeDialog = () =>
    showAccessCodeDialog = !showAccessCodeDialog;
  const accessCodeField = Form.field(Validate.required());

  const validateCodeCall = fromApi((accessCode: string) =>
    api.validateAccessCode({
      body: { accessCode },
      path: { id: spotGroup!.id },
    })
  );

  async function submitAccessCode() {
    if (!$accessCodeField.res.isOk()) {
      Toast.error("Fel i formulär");
      return;
    }
    const accessCode = $accessCodeField.res.value;
    const res = await $validateCodeCall.load(accessCode);
    if (res.error) {
      Toast.error(`Kunde inte validera ${spotGroup?.accessCodeRef?.name}`);
    } else if (!siteId) {
      Toast.error("Anläggning ID saknas");
    } else {
      push(spotClass.getCheckoutUrl({
        siteId,
        queueId,
        startDate,
        accessCode,
      }));
      showAccessCodeDialog = false;
    }
  }
</script>

<CardSection title={spotGroup ? spotGroup.name : spotClass.getName()}>
  {#snippet rightTitleElem()}
    {#if spotClass.isPopular}
      <Label shadow type="accent">Populär</Label>
    {/if}
  {/snippet}
  {#snippet content()}
    <BodyText>{spotClass.getDescription()}</BodyText>
    <Hor>
      <p>
        <strong>{spotClass.price.format()} kr</strong>
        / {agreementPeriod.format({ implicitMultiplier: true })}
      </p>
    </Hor>
    <Pill outset type={availabilityType}>
      {availabilityText}
    </Pill>
    <Spacer minSpace="s"></Spacer>
    <Hor>
      <Ver>
        {#if spotGroup}
          <p class="text-green-75">
            <span class="opacity-80">●</span> {spotClass.getName()}
          </p>
        {/if}
        {#each spotClass.getSellingPoints() as sp}
          <p class="text-green-75"><span class="opacity-80">●</span> {sp}</p>
        {/each}
      </Ver>
      {#if siteId && queueId}
        <Ver justify="end">
          {#if !requiresAccessCode}
            <Button
              disabled={!canClickButton}
              type={buttonType}
              nominwidth
              text={buttonText}
              link={spotClass.getCheckoutUrl({ siteId, queueId, startDate })}
            />
          {:else}
            <Button
              disabled={!canClickButton}
              type={buttonType}
              nominwidth
              text={buttonText}
              onClick={toggleAccessCodeDialog}
            ></Button>
          {/if}
        </Ver>
      {/if}
    </Hor>
  {/snippet}
</CardSection>

{#if requiresAccessCode && showAccessCodeDialog}
  <ModalDialog
    title={spotGroup.accessCodeRef?.name}
    onBackdropPressed={toggleAccessCodeDialog}
  >
    <ValInputField
      field={accessCodeField}
      label={spotGroup.accessCodeRef?.description ?? undefined}
      onEnter={submitAccessCode}
      onEscape={toggleAccessCodeDialog}
    />
    <Hor gap="m">
      <Button
        type="ghost"
        onClick={toggleAccessCodeDialog}
        text="Avbryt"
      />
      <Button
        type="primary"
        disabled={!$accessCodeField.res.isOk()}
        onClick={submitAccessCode}
        loading={$validateCodeCall.isLoading()}
        text="Gå vidare"
      />
    </Hor>
  </ModalDialog>
{/if}
